/**
 * Pick the previous weekly update for a narrative comparison.
 * Same schedule family (name with the date prefix and WU number removed).
 * Earlier means an earlier data date. When those dates match or are missing,
 * an earlier date prefix in the file name, then a lower WU number.
 */

export function scheduleFamilyKey (name) {
	let text = String(name || '').toLowerCase();
	text = text.replace(/\.(xer|xml|mpp)$/g, '');
	text = text.replace(/^\d{4}-\d{2}-\d{2}\s*/, '');
	text = text.replace(/\bwu\s*\d+\b/g, ' ');
	text = text.replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
	return text;
}

export function datasetName (dataset) {
	if (!dataset) return '';
	return dataset.Name || dataset.name || '';
}

export function datasetIdOf (dataset) {
	if (!dataset) return '';
	if (dataset.Id != null && dataset.Id !== '') return String(dataset.Id);
	if (dataset.id != null && dataset.id !== '') return String(dataset.id);
	return '';
}

export function parseDateMs (value) {
	const match = String(value || '').match(/(\d{4}-\d{2}-\d{2})/);
	if (!match) return 0;
	const ms = Date.parse(`${match[1]}T00:00:00Z`);
	return Number.isNaN(ms) ? 0 : ms;
}

export function fileDateMs (dataset) {
	return parseDateMs(datasetName(dataset));
}

export function wuNumber (name) {
	const match = String(name || '').toLowerCase().match(/\bwu\s*(\d+)\b/);
	if (!match) return null;
	const value = parseInt(match[1], 10);
	return Number.isNaN(value) ? null : value;
}

export function datasetDataDateMs (dataset) {
	if (!dataset) return 0;
	const projects = dataset.projects;
	if (projects && typeof projects === 'object') {
		let best = 0;
		Object.keys(projects).forEach(key => {
			const raw = projects[key] && projects[key].dataDate;
			const ms = parseDateMs(raw);
			if (ms > best) best = ms;
		});
		if (best) return best;
	}
	return parseDateMs(datasetName(dataset));
}

export function isEarlierUpdate (candidate, current) {
	const currentData = datasetDataDateMs(current);
	const candidateData = datasetDataDateMs(candidate);
	if (currentData && candidateData && currentData !== candidateData) {
		return candidateData < currentData;
	}
	const currentFile = fileDateMs(current);
	const candidateFile = fileDateMs(candidate);
	if (currentFile && candidateFile && currentFile !== candidateFile) {
		return candidateFile < currentFile;
	}
	const currentWu = wuNumber(datasetName(current));
	const candidateWu = wuNumber(datasetName(candidate));
	if (currentWu != null && candidateWu != null && currentWu !== candidateWu) {
		return candidateWu < currentWu;
	}
	return false;
}

function recencyRank (dataset) {
	const wu = wuNumber(datasetName(dataset));
	return [datasetDataDateMs(dataset), fileDateMs(dataset), wu == null ? -1 : wu];
}

function rankGreater (left, right) {
	for (let i = 0; i < left.length; i++) {
		if (left[i] === right[i]) continue;
		return left[i] > right[i];
	}
	return false;
}

export function suggestPriorDataset (current, datasets) {
	const currentId = datasetIdOf(current);
	const family = scheduleFamilyKey(datasetName(current));
	if (!family) return '';
	let bestId = '';
	let bestRank = null;
	Object.keys(datasets || {}).forEach(key => {
		const row = datasets[key];
		const id = datasetIdOf(row) || String(key);
		if (!id || id === currentId) return;
		if (scheduleFamilyKey(datasetName(row)) !== family) return;
		if (!isEarlierUpdate(row, current)) return;
		const rank = recencyRank(row);
		if (bestRank === null || rankGreater(rank, bestRank)) {
			bestRank = rank;
			bestId = id;
		}
	});
	return bestId;
}

export function priorSelectionHint (priorId, suggestedId) {
	const prior = String(priorId || '');
	const suggested = String(suggestedId || '');
	if (prior && suggested && prior === suggested) {
		return {
			kind: 'suggested',
			text: 'Suggested from the same project and the previous data date.'
		};
	}
	if (prior) {
		return {
			kind: 'selected',
			text: 'Using the prior update you selected.'
		};
	}
	if (suggested) {
		return {
			kind: 'suggested',
			text: 'Suggested from the same project and the previous data date.'
		};
	}
	return {
		kind: 'none',
		text: 'No earlier update of this project was found. Slips and week-over-week earned value will say they cannot be determined.'
	};
}

export function priorDatasetOptions (current, datasets) {
	const currentId = datasetIdOf(current);
	const family = scheduleFamilyKey(datasetName(current));
	const rows = [];
	Object.keys(datasets || {}).forEach(key => {
		const row = datasets[key];
		const id = datasetIdOf(row) || String(key);
		if (!id || id === currentId) return;
		rows.push({
			id,
			name: datasetName(row) || `Dataset ${id}`,
			sameProject: !!(family && scheduleFamilyKey(datasetName(row)) === family),
			dataDateMs: datasetDataDateMs(row)
		});
	});
	rows.sort((a, b) => {
		if (a.sameProject !== b.sameProject) return a.sameProject ? -1 : 1;
		return b.dataDateMs - a.dataDateMs;
	});
	return rows;
}
